import { create } from 'zustand';
import { apiClient } from '@/lib/api';

/** Los datos de la empresa de la sesión (`GET /api/tenant`). */
export interface Empresa {
  id: string;
  name: string;
  slug: string | null;
  nit: string | null;
  address: string | null;
  country: string | null;
  phone: string | null;
  email: string | null;
  website: string | null;
  companySize: string | null;
  industrySegment: string | null;
  /** PNG/JPEG en data URL o una URL https (lo valida el servidor). */
  logoUrl: string | null;
  brandColor: string | null;
  decimalPlaces: number | null;
  /** Mi empresa › Apariencia: el degradado de la plataforma (null = Prixia). */
  uiTheme: Apariencia | null;
  /** Mi empresa › Documentos: { colorTotales, tipografia }. */
  documentTemplate: PlantillaGuardada | null;
  /** Mi empresa › Pagos y facturación. */
  billingInfo: DatosDeFacturacion | null;
}

export const APARIENCIAS = ['prixia', 'azul', 'verde', 'rojo', 'negro'] as const;
export type Apariencia = (typeof APARIENCIAS)[number];

export const TIPOGRAFIAS = ['poppins-inter', 'inter', 'roboto'] as const;
export type Tipografia = (typeof TIPOGRAFIAS)[number];

export interface PlantillaGuardada {
  colorTotales?: string;
  tipografia?: Tipografia;
}

export interface DatosDeFacturacion {
  razonSocial?: string;
  nit?: string;
  correo?: string;
  copiaA?: string;
  ciudad?: string;
  direccion?: string;
  debitoAutomatico?: boolean;
}

export type CambioDeEmpresa = Partial<
  Pick<
    Empresa,
    | 'name'
    | 'nit'
    | 'address'
    | 'country'
    | 'phone'
    | 'email'
    | 'website'
    | 'logoUrl'
    | 'brandColor'
    | 'uiTheme'
    | 'documentTemplate'
    | 'billingInfo'
  >
>;

interface EstadoEmpresa {
  empresa: Empresa | null;
  /** De qué empresa es lo cargado: la misma pestaña puede cambiar de portal. */
  deTenant: string | null;
  cargando: boolean;
  cargar: (tenantId: string, forzar?: boolean) => Promise<void>;
  guardar: (cambio: CambioDeEmpresa) => Promise<Empresa>;
}

/**
 * Una sola copia de la empresa para toda la app: el menú lateral muestra su
 * logo («Prixia × tu logo», maqueta de Melanie) y Mi empresa la edita. Cuando
 * Mi empresa guarda, el menú se entera sin recargar.
 */
export const useEmpresaStore = create<EstadoEmpresa>((set, get) => ({
  empresa: null,
  deTenant: null,
  cargando: false,
  cargar: async (tenantId, forzar = false) => {
    const { deTenant, cargando } = get();
    if (!forzar && (cargando || deTenant === tenantId)) return;
    set({ cargando: true });
    try {
      const empresa = await apiClient.get<Empresa>('/api/tenant');
      set({ empresa, deTenant: tenantId });
    } finally {
      set({ cargando: false });
    }
  },
  guardar: async (cambio) => {
    const empresa = await apiClient.patch<Empresa>('/api/tenant', cambio);
    set({ empresa });
    return empresa;
  },
}));
